import React, { useRef, useEffect, useState, useCallback, useContext } from 'react';
import CustomAccordion from '@v2/components/ui/CustomAccordion/CustomAccordion';
import { useDispatch } from 'react-redux';

import Clickable from '@v2/components/common/presentational/clickables/default';
import {
    addNewIncompleteBox, setActiveBoxBlueprint, resetChildBox, setActiveBox,
    updateCartProducts, emptyCart, useAddActiveBoxToCart, updateActiveBox
} from '@app/store/rtk.cart.store';
import ProductService from '@services/product.service';
import { useHistoryWithReferrer } from '@utils/history';
import { IBoxBlueprint, ICartBoxLine, ICartBox, IProduct } from "@interfaces/_cart";
import { packAndTrack, trackCartBox } from '@v2/services/analytics.service';
import SubscriptionService from '@v2/services/subscription.service';
import { useUpgradeBoxModal, useAddCartModal } from '@components/common/presentational/modal/common';
import { useIsMobile } from '@components/common/helpers/screen-check';
import { WPImage } from '@v2/utils/image';

import Loader, { ContentLoader } from '@components/common/presentational/loader/loader';

import BuilderProducts from '@components/common/builder/products';
import Box from '@v2/components/common/builder/box';
import NutrientsFilter from '@v2/components/common/presentational/nutrients/nutrients-filter';
import Star from '@v2/components/common/recommendations/star';

import BoxIcon from '@v2/components/common/products/modals/box-icon';
import iconResetToRecommendation from '@img/shop/reset-to-recommendation.svg';
//import iconQuestion from '@img/shop/question.svg';
import arrowUp from '@img/checkout/arrow-up.svg';
import arrowDown from '@img/checkout/arrow-down.svg';

import styles from '@components/common/builder/styles';
import style from '@components/common/builder/recommended-for.styles';
import { imgV2 as img } from '@styles/utils';

import { scrollIntoView } from '@v2/utils/scroll';
import { useIsSwapping } from '@components/common/products/utils';
import { setSubscription } from '@app/store/rtk.subscription.store';
import { setEntities } from '@app/store/rtk.entities.store';
import { useEntity } from '@app/store/rtk.entities.selectors';
import { useActiveBox, useActiveBoxIndex, useActiveChildBox, useIsActiveBoxFull } from '@app/store/cart.selectors';
import { useIsLogged, useJWT } from '@app/store/rtk.user.selectors';
import { BuilderHeader } from '../presentational/header/header.modal';
import { hideGeneralModal, popShopModal } from '@v2/store/rtk.modals.store';
import { RouterContext } from '@v2/routes';

// import { useScrollPosition } from '@n8tb1t/use-scroll-position'

interface IShopCategory {
    id: string,
    title: string,
    pricingType?: string,
    pricePerMeal?: number,
    ref: React.MutableRefObject<any>,
    disabled: boolean,
    products: object,
}

function useCategories(): IShopCategory[] {
    const categories = useEntity('categories');
    const products = {
        pouch: useEntity('pouches') || {},
        bar: useEntity('bars') || {},
        smoothie: useEntity('smoothies') || {},
        bonebrothpouch: useEntity('bonebrothpouches') || {},
    }

    const pouchlike = [];

    if (products.bonebrothpouch.length > 0) {
        for (const key in products.bonebrothpouch) {
            pouchlike.push(products.bonebrothpouch[key])
        }
    }

    if (products.pouch.length > 0) {
        for (const key in products.pouch) {
            pouchlike.push(products.pouch[key])
        }
    }

    const availableCategories: { [key: string]: IShopCategory } = {
        'pouches': {
            id: 'pouches',
            title: 'Purees',
            pricingType: 'each',
            pricePerMeal: Object.keys(pouchlike).length > 0
                ? (Object.values(pouchlike).shift() as IProduct).priceSubscribed
                : null,
            ref: useRef(null),
            disabled: false,
            products: pouchlike
        },
        'bars': {
            id: 'bars',
            title: 'Smart Bars',
            pricingType: '5-pack',
            pricePerMeal: products.bar && Object.keys(products.bar).length > 0
                ? (Object.values(products.bar).shift() as IProduct).priceSubscribed
                : null,
            ref: useRef(null),
            disabled: false,
            products: products.bar
        },
        'smoothies': {
            id: 'smoothies',
            title: 'Dairy-Free Smoothies',
            pricingType: 'each',
            pricePerMeal: products.smoothie && Object.keys(products.smoothie).length > 0
                ? (Object.values(products.smoothie).shift() as IProduct).priceSubscribed
                : null,
            ref: useRef(null),
            disabled: false,
            products: products.smoothie
        },
        /*
        'peppa': {
            id: 'peppa',
            title: 'Cerebelly x Peppa Pig',
            pricingType: 'each',
            pricePerMeal: prices ? prices.pouches : null,
            ref: useRef(null),
            disabled: true,
            products: products.pouch
                ? Object.assign({}, products.pouch, products.bar, products.smoothie)
                : {}
        }*/
    };

    const categoryData: IShopCategory[] = [];

    for (let key in categories) {
        if (availableCategories[categories[key].slug]) {
            categoryData.push(availableCategories[categories[key].slug]);
        }
    }

    return categoryData;
}

function BoxSizePicker({ setBuilding }: { setBuilding: Function }) {
    const dispatch = useDispatch();
    const blueprints = useEntity('blueprints');
    const box = useActiveBox();
    const [activeBlueprint, setActiveBlueprint] = useState(null);

    const createBox = (blueprint: IBoxBlueprint) => {
        dispatch(addNewIncompleteBox());
        dispatch(setActiveBoxBlueprint({ box: { blueprint, boxType: 'custom', title: 'Custom Bundle' }}));
        setBuilding(true);
    }

    const changeBoxBlueprint = (blueprint: IBoxBlueprint) => {
        dispatch(setActiveBoxBlueprint({ box: { blueprint }}));
        setBuilding(true);
    }

    useEffect(() => {
        if (!box || !box.blueprint) {
            return;
        }

        packAndTrack('Select Bunlde Size Viewed', [{ 'view': 'Shop', 'section': 'Build Bundle' }]);
        setActiveBlueprint(box.blueprint);
    }, []);

    return (
        <ContentLoader loading={!blueprints}>
            <div className={styles.initial}>
                <h3>MAKE IT YOURS</h3>
                <h1>Select a bundle size</h1>
                <div className="blueprints">
                    {blueprints ?
                        Object.values(blueprints)
                            .sort((a: IBoxBlueprint, b: IBoxBlueprint) => a.minItems - b.minItems)
                            .filter((blueprint: IBoxBlueprint) => !!blueprint.minItems && blueprint.minItems >= 14)
                            .map((blueprint: IBoxBlueprint, index: number) =>
                                <Clickable
                                    key={index}
                                    onClick={() => {
                                        setActiveBlueprint(blueprint);
                                    }}
                                    className={`blueprint ${(activeBlueprint && activeBlueprint.name && blueprint.name === activeBlueprint.name) ? 'active' : ''}`}>
                                    <BoxIcon type={blueprint.type} />
                                    <div className="name">{blueprint.name}</div>
                                    <div className="size">{blueprint.minItems} items</div>
                                </Clickable>
                            ) : null}
                </div>
                <button
                    disabled={!activeBlueprint}
                    className="button primary blue"
                    aria-label={'Continue'}
                    onClick={() => {
                        if (box) {
                            changeBoxBlueprint(activeBlueprint);
                            packAndTrack('Bundle Size Changed',
                                [{ 'view': 'Shop', 'section': 'Build Bundle', 'blueprint': activeBlueprint.name }]
                            );
                        } else {
                            createBox(activeBlueprint);
                            packAndTrack('Bundle Size Selected',
                                [{ 'view': 'Shop', 'section': 'Build Bundle', 'blueprint': activeBlueprint.name }]
                            );
                        }
                    }}>
                    Continue
                </button>
            </div>
        </ContentLoader>
    );
}

function isRoute(route: string) {
    return window.location.pathname.startsWith(route);
}

function FilterNav({ categories, filterable, filterBy, setFilterBy, isMobile }
    : { categories: any, filterable: any, filterBy: any, setFilterBy: Function, isMobile: boolean }) {
    return (
        <div className={`${`filter-actions`} ${isMobile ? 'mobile' : ''} ${filterBy ? 'with-reset' : ''}`}>
            {categories && filterBy &&
                <button onClick={() => setFilterBy(null)} className="button secondary reset">Reset</button>
            }
            <div className="nutrient">
                {categories &&
                    <NutrientsFilter
                        products={filterable}
                        filterBy={filterBy}
                        setFilterBy={setFilterBy} />
                }
            </div>
            {categories && filterBy &&
                <button onClick={() => setFilterBy(null)} className="button secondary reset mobile">Reset</button>
            }
        </div>
    )
}

function UpgradeBox({ showSizeScreen, onAddBox, showBoxScreen, hideUpgradeBoxScreen, isEditing, isSwapping }:
    { showSizeScreen: Function, onAddBox: Function, showBoxScreen: Function, hideUpgradeBoxScreen: Function, isEditing: boolean, isSwapping?: boolean }) {
    return (
        <div className={styles.upgradeBox}>
            <h3>Your bundle is full</h3>
            <button onClick={() => onAddBox()} className="button primary blue mt-3">
                {isEditing ? 'Confirm box changes' : (isSwapping ? 'Add to replacements' : 'Add to cart')}
            </button>
            <button onClick={() => showBoxScreen()} className="button secondary blue mt-2">
                View bundle
            </button>
            <button onClick={() => showSizeScreen()} className="button secondary blue mt-2">
                Change size
            </button>
            <button onClick={() => hideUpgradeBoxScreen()} className="button primary link blue mt-4">
                Keep editing
            </button>
        </div>
    );
}

function Builder({ hide, setBuilding, isEditing }: { hide: Function, setBuilding: Function, isEditing: boolean }) {
    const dispatch = useDispatch();
    const addActiveBoxToCart = useAddActiveBoxToCart();
    const blueprints = useEntity('blueprints');
    const history = useHistoryWithReferrer();
    const isLogged = useIsLogged();
    const box = useActiveBox();
    const boxIndex = useActiveBoxIndex();
    const categories = useCategories();
    const isBoxMaxed = useIsActiveBoxFull();
    const [showCartModal] = useAddCartModal(boxIndex);
    const mobile = useIsMobile();
    const isSwapping = useIsSwapping();

    const [filterBy, setFilterBy] = useState(null);
    const [addedCallback, setAddedCallback] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [mobileBox, setMobileBox] = useState(false);

    const [showUpgradeBoxMobile, setShowUpgradeBoxMobile] = useState(false);
    const [showUpgradeBoxModal] = useUpgradeBoxModal(() => addBox(), () => setBuilding(false));

    const filterable = Object.assign({},
        useEntity('pouches') || {},
        useEntity('bars') || {},
        useEntity('smoothies') || {},
        useEntity('bonebrothpouches') || {}
    );

    const ref = useRef(null);
    const ref2 = useRef(null);

    const showRecommendation = !processing && box &&
        box.child && box.stage &&
        ((!box.child.isVegan && box.stage.lines) ||
            (box.child.isVegan && box.stage.veggieLines));

    const addBox = async () => {
        setProcessing(true);

        if (isLogged && box.id) {
            await SubscriptionService.updateUserBox(box)
                .then((subscription) => {

                    dispatch(setSubscription(subscription));
                    dispatch(emptyCart());

                    packAndTrack(
                        box.id ? 'Edit Box Finished' : 'Add To Subscription Clicked',
                        [box],
                        [['count', 'price']]);

                    history.pushWithReferrer(`/subscription${box?.id ? '/' + box.id : ''}`);
                });
        } else {
            if (!isSwapping && !isEditing) {
                showCartModal();
            }

            addActiveBoxToCart();
            trackCartBox(box);

            // For edit box from card only
            if (isEditing) {
                hide();
                return;
            }

            history.pushWithReferrer(isSwapping ? '/swap/confirm' : '/shop');
        }
    }

    useEffect(() => {
        if (!box || !box.blueprint || box.blueprint.type !== 'mini') {
            return;
        }

        const blueprint = Object.values(blueprints)
            .find((blueprint: IBoxBlueprint) => blueprint.type === 'small') as IBoxBlueprint;

        if (!blueprint) {
            return;
        }

        dispatch(setActiveBoxBlueprint({ box: { blueprint: blueprint }}));
    }, [boxIndex]);

    useEffect(() => {
        if (addedCallback && isBoxMaxed) {
            if (mobile) {
                setShowUpgradeBoxMobile(true)
            }
        }
        setAddedCallback(false);
    }, [addedCallback]);

    return (
        <div className="shop-wrapper" ref={ref} style={{ overflow: 'clip' }}>
            <div className="shop-products-wrapper">
                <div className={styles.boxTabsMobile} >
                    <BoxEditMobile
                        mobileBox={mobileBox}
                        setMobileBox={setMobileBox} />
                    <BoxCounterMobile
                        box={box}
                        mobileBox={mobileBox}
                        setMobileBox={setMobileBox}
                    />
                </div>
                <div className={`shop-products-container ${mobileBox ? 'hide-mobile' : 'show-mobile'}`}>
                    <ContentLoader loading={!categories}>
                        <div className="filters">
                            <div className="filter-row">
                                <ul className="filter category">
                                    {
                                        categories.map((category, key) =>
                                            <li key={key}
                                                //className={category.id === activeTab ? 'active' : ''}
                                                className="active"
                                                onClick={() => scrollIntoView('category-' + category.id)}>
                                                {category.title}
                                            </li>
                                        )
                                    }
                                </ul>
                            </div>

                            <div className="separator">
                                <span></span>
                            </div>

                            <FilterNav
                                categories={categories}
                                filterBy={filterBy}
                                setFilterBy={setFilterBy}
                                filterable={filterable}
                                isMobile={false} />
                        </div>

                        <FilterNav
                            categories={categories}
                            filterBy={filterBy}
                            setFilterBy={setFilterBy}
                            filterable={filterable}
                            isMobile={true} />

                        {showRecommendation && <Recommendation />}

                        <div className="products-wrapper">
                            <>
                                {
                                    categories.map((category, key) => {
                                        return (!category.disabled) &&
                                            <div
                                                ref={category.ref}
                                                key={key}
                                                className={`element-${category.id}`}>
                                                <BuilderProducts
                                                    category={category}
                                                    filterBy={filterBy}
                                                    setAddedCallback={setAddedCallback}
                                                    processing={processing}
                                                    isEditing={isEditing}
                                                    showUpgradeBoxModal={showUpgradeBoxModal}
                                                />
                                            </div>
                                    })
                                }
                                <div ref={ref2}></div>
                            </>
                        </div>
                    </ContentLoader>
                </div>

                <div className={`shop-cart ${mobileBox ? 'show-mobile' : 'hide-mobile'}`}>
                    <Box
                        isLogged={isLogged}
                        resume={() => {
                            if (!isRoute('/shop')) {
                                history.pushWithReferrer('/shop');
                            }
                        }}
                        showSizeScreen={() => setBuilding(false)}
                        onAddBox={addBox}
                        setAddedCallback={setAddedCallback}
                        processing={processing}
                        isEditing={isEditing}
                    />
                </div>

                {showUpgradeBoxMobile &&
                    <UpgradeBox
                        showSizeScreen={() => {
                            setShowUpgradeBoxMobile(false);
                            setBuilding(false);
                        }}
                        onAddBox={() => {
                            setShowUpgradeBoxMobile(false);
                            addBox();
                        }}
                        showBoxScreen={() => {
                            setShowUpgradeBoxMobile(false);
                            setMobileBox(true);
                        }}
                        hideUpgradeBoxScreen={() => setShowUpgradeBoxMobile(false)}
                        isEditing={isEditing}
                        isSwapping={isSwapping}
                    />
                }
            </div>
        </div>
    );
}

function Recommendation() {
    const dispatch = useDispatch();
    const box = useActiveBox();
    const boxIndex = useActiveBoxIndex();
    const [active, setActive] = useState(null);

    const handleFillWithRecommendation = useCallback(() => {
        dispatch(resetChildBox({ isVegan: box.child.isVegan }));
    }, []);


    return (
        <CustomAccordion key={'1'} activeKey={`${active}`} className={`${style.recommendedForContainer}`}>
            <div className="recommended-for-header">
                <CustomAccordion.Toggle
                    className="recommended-for-control toggle"
                    onClick={() => setActive(active === 1 ? null : 1)}
                    eventKey={'1'}>
                    <Star name={box.child.name} index={boxIndex} />
                    <span>Recommended for {box.child.name}</span>
                    <button className="button">
                        <img src={active === 1 ? arrowDown : arrowUp} alt="Icon Dropdown" />
                    </button>
                </CustomAccordion.Toggle>
                <button className="button blue reset-for desktop" onClick={handleFillWithRecommendation}>
                    <img src={iconResetToRecommendation} alt="Fill with recommendation" />
                    Fill with recommendation
                </button>
            </div>
            <CustomAccordion.Collapse eventKey={'1'}>
                <div className='recommended-for-body'>
                    <div className="recommended-for-product-wrapper">
                        <div className="recommended-for-products">
                            {
                                Object.values(box.child.isVegan ? (box.stage.veggieLines || {}) : (box.stage.lines || {}))
                                    .map((line: ICartBoxLine, key: number) =>
                                        <div key={key} className={`recommended-for-single-product ${line.product.category}`}>
                                            <WPImage src={line.product.image} alt={line.product.title} />
                                            <span className="recommended-for-product-quantity">{line.count}</span>
                                        </div>
                                    )
                            }
                        </div>
                        {/*
                            <div className="recommended-for-see-why">
                                <img src={iconQuestion} alt="Icon Question" />
                                <span>See why</span>
                            </div>
                            */}
                    </div>
                </div>
            </CustomAccordion.Collapse>
            <button className="button blue reset-for mobile" onClick={() => dispatch(resetChildBox({ isVegan: box.child.isVegan }))}>
                <img src={iconResetToRecommendation} alt="Fill with recommendation" />
                Fill with recommendation
            </button>
        </CustomAccordion>
    )
}

function BoxCounterMobile({ box, mobileBox, setMobileBox }: { box: ICartBox, mobileBox: boolean, setMobileBox: Function }) {
    return (
        <Clickable className={`${`tab`} ${mobileBox === true ? 'active' : ''}`} onClick={() => setMobileBox(true)}>
            <div className="wrapper">
                {(box && box.blueprint.minItems)
                    ?
                    <>
                        <img src={img(`shop/builder/${box.count === box.blueprint.minItems ? 'pack-green' : 'pack'}.svg`)} alt={'Count Icon'} height="24" />
                        <div className={`text ${box.count === box.blueprint.minItems ? 'full' : ''}`}>
                            {box.count}/ {box.blueprint.minItems} items
                        </div>
                    </>
                    :
                    <img src={img(`shop/builder/pack.svg`)} alt={'Count Icon'} height="24" />
                }
            </div>
        </Clickable>
    )
}

function BoxEditMobile({ mobileBox, setMobileBox }: { mobileBox: boolean, setMobileBox: Function }) {
    return (
        <Clickable className={`${`tab`} ${mobileBox === false ? 'active' : ''}`} onClick={() => setMobileBox(false)}>
            <div className="wrapper">
                <img src={img('shop/builder/edit.svg')} alt={'Edit Icon'} height="24" />
                <div className="text">Edit</div>
            </div>
        </Clickable>
    )
}


export default function BuilderModal({ hide, isActive, isEditing, reset }:
    { hide: Function, isActive?: boolean, isEditing?: boolean, reset?: boolean }
) {
    const dispatch = useDispatch();
    const childBox = useActiveChildBox();
    const editingChildBox = (childBox?.survey?.completed && childBox?.incomplete);
    const [building, setBuilding] = useState((editingChildBox || isActive) ? true : false);
    const [loaded, setLoaded] = useState(false);
    const categories = useEntity('categories');
    const activeBox = useActiveBox();
    const initialBox = useRef(activeBox);
    const history = useHistoryWithReferrer();
    const route = useContext(RouterContext);
    const jwt = useJWT();

    const handleFillWithRecommendation = useCallback(() => {
        dispatch(resetChildBox({ isVegan: activeBox.child.isVegan }));
    }, []);

    const handleResetToInitial = useCallback(() => {
        switch(history.location.pathname) {
            case '/survey':
                handleFillWithRecommendation();
                if(!jwt) {
                    dispatch(popShopModal());
                }
                break;
            case '/shop':
            case '/shop/':
                if(route.from.includes('/survey') && route.to.includes('/box-builder')) {
                    handleFillWithRecommendation();
                    dispatch(hideGeneralModal());
                    break;
                }
                if(route.from.includes('/shop') && route.to.includes('/box-builder')) {
                    try {
                        handleFillWithRecommendation();
                    } catch(e) {}
                    break;
                }
                if(route.from === '' && route.to === '') {
                    history.pushWithReferrer('/cart');
                    dispatch(updateActiveBox({ box: initialBox.current }));
                    dispatch(hideGeneralModal());
                }
                break;
        }
    }, [])

    const handleGoBack = useCallback(() => {
        if(isEditing) {
            dispatch(updateActiveBox({ box: initialBox.current }));
        }
        hide();
    }, [isEditing]);

    useEffect(() => {
        window.addEventListener('popstate', handleResetToInitial, { once: true });
        return () => {
            setTimeout(() => {
                window.removeEventListener('popstate', handleResetToInitial);
            })
        }
    }, [handleResetToInitial])

    useEffect(() => {
        (async function () {
            if (!categories) {
                await ProductService.list()
                    .then(data => {
                        dispatch(setEntities(data));
                        dispatch(updateCartProducts({ data }));
                    })
                    .catch(console.error);
            }

            if (reset && !editingChildBox) {
                dispatch(setActiveBox({ index: null }));
                setBuilding(false);
            }
            setLoaded(true);
        })();
    }, []);

    return (
        <>
            <BuilderHeader back={handleGoBack} />
            <div className={`${styles.container} leave-it-here-for-now`} >
                {loaded
                    ?
                    <>
                        {building
                            ?
                            <Builder hide={hide} isEditing={isEditing} setBuilding={setBuilding} />
                            :
                            <BoxSizePicker setBuilding={setBuilding} />
                        }
                    </>
                    :
                    <Loader />
                }
            </div>
        </>
    );
}